'use client';

import React from 'react';
import { useMustacheTranslation, useOTSTranslation } from '@/i18n/useOTSTranslation';
import { SidebarAd } from '@/tracking/ads/components/SidebarAd';
import {
  IRegionResortsPaginated, IRegionShort, IRegionFeaturedResort,
} from '@/api/region/types';
import { Main } from '@/components/resort/Layout/Main';
import { Side } from '@/components/resort/Layout/Side';
import { useRegionLayoutBubble } from '@/page-components/region/_context';
import { IRegionLayout } from '@/page-components/region/_layout';
import { RouteAndAnalyticsProps } from '@/page-components/pathTypes';
import { IRelatedNews, ResortStatus } from '@/api/resort/types';
import { Routes } from '@/routes';
import PlanYourTrip from '@/components/common/PlanYourTrip/RegionWidget';
import { Hero400Error } from '@/components/400';
import { formatDateFromStr, MINIMAL_DATE_FORMAT } from '@/util/formatDate';
import { spansMultipleRegions } from '@/util/spansMultipleRegions';
import RelatedArticles from '@/components/resort/RelatedArticles';
import { SkiGptPromotion } from '@/components/common/SkiGptPromotion';
import { PageBody } from '@/components/common/PageBody';
import { SeoSection } from '@/components/common/SeoSection';
import { Text } from '@/components/ui/text';
import { ProjectedOpeningTable } from '@/components/region/StatusTables';
import { LoadMoreRows } from '@/components/region/LoadMoreRows';
import { useLoadMoreResorts } from '@/components/region/useLoadMoreResorts';
import { fetchRegionResortsByStatus } from '@/api/region/static';
import { REGION_RESORT_PAGE_SIZE } from '@/ots-constants';

export type RegionProjectedOpeningPageProps = {
  resorts: IRegionResortsPaginated,
  pathInfo: IRegionShort,
  openingDate: string,
  numResorts: number,
  relatedNews: IRelatedNews | null,
  featured: IRegionFeaturedResort,
} & RouteAndAnalyticsProps & IRegionLayout;

export const DEBUG_TAG = 'region-projected-openings-page';
export const TEMPLATE_ID = '2.1.5';
export const TEMPLATE_NAME = 'Region Projected Opening';

export default function RegionProjectedOpeningsPage({
  resorts, numResorts, openingDate, pathInfo, relatedNews,
}: RegionProjectedOpeningPageProps) {
  useRegionLayoutBubble({});
  const { t } = useOTSTranslation('common');
  const m = useMustacheTranslation('common');

  if (!resorts) {
    return <Hero400Error light />;
  }

  const seoTitle = pathInfo.isLogical ? t('region.openDates.logicSeoTitle', {
    region: pathInfo.title,
  }) : t('region.openDates.seoTitle', {
    region: pathInfo.title,
  });

  const seoText = pathInfo.isLogical ? m('region.openDates.logicSeoText', {
    region: pathInfo.title,
    numresorts: numResorts,
    firstopendate: formatDateFromStr(openingDate, MINIMAL_DATE_FORMAT),
  }) : m('region.openDates.seoText', {
    region: pathInfo.title,
    numresorts: numResorts,
    firstopendate: formatDateFromStr(openingDate, MINIMAL_DATE_FORMAT),
  });

  const fetchPage = React.useCallback(
    (page: number) => fetchRegionResortsByStatus(
      pathInfo.uuid, ResortStatus.openingDates, page, REGION_RESORT_PAGE_SIZE,
    ),
    [pathInfo.uuid],
  );
  const {
    rows, hasMore, isLoading, loadMore, loadAll,
  } = useLoadMoreResorts({
    initial: resorts,
    fetchPage,
    label: `region ${pathInfo.uuid} projected-openings`,
  });

  // Judged on the server-rendered rows so a later page cannot flip the
  // decision once the reader has started scrolling.
  const showRegion = spansMultipleRegions(resorts.data ?? []);

  return (
    <>
      <Main>
        <PageBody>
          <SeoSection title={seoTitle}>{seoText}</SeoSection>

          {rows.length > 0 ? (
            <>
              <ProjectedOpeningTable
                rows={rows}
                linkTo={(r) => Routes.resortTripPlanningOverview(r)}
                dateLabel={t('region.openDates.projectedDate')}
                showRegion={showRegion}
                onSortIntent={loadAll}
                isLoading={isLoading}
              />
              <LoadMoreRows hasMore={hasMore} isLoading={isLoading} onLoadMore={loadMore} />
            </>
          ) : (
            <Text variant="default" className="text-muted-foreground">
              {t('common.na')}
            </Text>
          )}

          <SkiGptPromotion
            templateId={`${TEMPLATE_ID}-region-openings`}
            name={pathInfo.title}
            regionId={pathInfo.uuid}
          />

          <PlanYourTrip
            region={pathInfo}
            hasSkiRental={false}
            hasCarRental={false}
            hasFlights={false}
            isPartner={false}
          />
        </PageBody>
      </Main>
      <Side>
        <RelatedArticles
          relatedNewsList={relatedNews}
        />
        <SidebarAd isRegion id={pathInfo.uuid} showWeSki={false} />
      </Side>
    </>
  );
}

// fetchProps moved to ./<filename>.server.ts so the App Router RSC page can
// import the data fetcher without pulling in this 'use client' bundle.
